import {
  AllEntitiesResolved,
  Entity,
  EntityResolved,
  EntitySearchType,
  formatTicker,
} from '@api';
import { Dropdown } from '@design/components/dropdown/Dropdown';
import { Icon } from '@design/components/icon/Icon';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { useTagsSearch } from '@terminal/hooks/use-tags-search/useTagsSearch';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityDropdownItem } from '../../entity-dropdown-item/EntityDropdownItem';
import styles from './EntityCell.module.scss';

export interface EntityCellBaseHandlers {
  onChangeEntity: (
    prevEntity: EntityResolved,
    newEntity: EntityResolved
  ) => void;
  onAddEntity: (entity: EntityResolved) => void;
  onRemoveEntity: (entity: EntityResolved) => void;
}

const EntityCellBase = ({
  entity,
  handlers,
}: {
  entity: AllEntitiesResolved;
  handlers: EntityCellBaseHandlers;
}) => {
  const { t } = useTranslation(['watchlistManager', 'analyze']);
  const [shouldShowDropdownItems, setShouldShowDropdownItems] = useState(false);

  const { tags: tagList, searchTags } = useTagsSearch({
    searchType: EntitySearchType.Entity,
  });
  const { onChangeEntity, onRemoveEntity, onAddEntity } = handlers;

  const hasInput = !!entity.input;
  const hasEntityData = !!entity.tag;
  const parsedEntity = entity as Entity;
  const entityTicker = hasEntityData
    ? parsedEntity.ticker.toLowerCase()
    : entity.input;

  const filteredTagList = useMemo(
    () =>
      tagList.filter(
        t =>
          t.tag.split('_')[0].toLowerCase() === entityTicker ||
          t.ticker === entityTicker
      ),
    [tagList, entityTicker]
  );
  const hasAlternatives = hasInput
    ? !!filteredTagList.length
    : filteredTagList.length > 1;
  const isUnmappedWithEntityData = !hasEntityData && hasAlternatives;

  useEffect(() => {
    searchTags(entityTicker);
  }, [entityTicker]);

  const getContent = () => {
    if (!hasEntityData) {
      if (hasAlternatives) {
        return (
          <div className={styles.noneOfTheAboveLabel}>
            {t('watchlistManager:noneOfTheAbove')}
          </div>
        );
      }

      return (
        <span className={styles.disabledText}>
          {t('watchlistManager:notAvailable')}
        </span>
      );
    }

    const ticker = formatTicker(parsedEntity);
    return (
      <div className={styles.logoAndTickerWrapper}>
        <AssetLogoContainer
          className={styles.styledAssetLogoContainer}
          entity={parsedEntity}
          size={20}
        />
        <TruncateTooltip
          atLength={17}
          text={ticker}
          truncatedLabelWrapper={<span className={styles.text}>{ticker}</span>}
        />
      </div>
    );
  };

  const handleSelectItem = (newEntity: Entity) => {
    if (isUnmappedWithEntityData) {
      onAddEntity({ ...newEntity, input: entity.input });
    } else {
      onChangeEntity(
        { ...parsedEntity, input: entity.input },
        { ...newEntity, input: entity.input }
      );
    }
  };

  const onNoneOfTheAboveClick = () => {
    setShouldShowDropdownItems(false);
    onRemoveEntity({ ...parsedEntity, input: entity.input });
  };

  if (!hasAlternatives) {
    return <div className={styles.root}>{getContent()}</div>;
  }

  return (
    <div className={styles.root}>
      <Dropdown<Entity>
        className={clsx(
          styles.styledDropdown,
          'entity-watchlist-dropdown'
        )}
        shouldShowItems={shouldShowDropdownItems}
        dropdownItems={filteredTagList}
        onItemsShowChange={setShouldShowDropdownItems}
        selectItem={handleSelectItem}
        inPortal
        strategy="fixed"
        placement="bottom-start"
        activeItemIdx={
          filteredTagList.some(e => e.tag === entity.tag)
            ? filteredTagList.findIndex(e => e.tag === entity.tag)
            : undefined
        }
        renderDropdownItem={(item, isActive, isFocused) => (
          <EntityDropdownItem
            entity={item}
            isActive={isActive}
            isFocused={isFocused}
          />
        )}
        afterInListNode={
          !isUnmappedWithEntityData &&
          hasInput && (
            <div
              className={styles.noneOfTheAboveWrapper}
              onClick={onNoneOfTheAboveClick}
              data-testid="none-of-the-above-button"
            >
              <div className={styles.noneOfTheAboveContentWrapper}>
                {t('watchlistManager:noneOfTheAbove')}
              </div>
            </div>
          )
        }
      >
        <div className={styles.triggerWrapper}>
          <div className={styles.contentWrapper}>{getContent()}</div>
          <Icon
            className={styles.styledIcon}
            iconName="ChevronLightDown"
            size={16}
          />
        </div>
      </Dropdown>
    </div>
  );
};

export const EntityCell = React.memo(EntityCellBase);
